import {_} from 'lodash';
import {IMessageData, IObjectData, ISearchObjectParams} from "../../../index";
import * as ko from "knockout";
import {IComponentDialogParams, IDialog} from "../../modal/component";
import {KOSubscriptionTracker} from "../../../util/KOSubscriptionTracker";
import {MAX_CONCURRENT_REQUESTS, ObjectTypeId} from "../../../constants";
import {
    createErrorMessage,
    createErrorString,
    getAnyValue,
    getObjectDefinition,
    IObjectDefinition
} from "../../../util/misc";
import PromiseRequest from "../../../req/PromiseRequest";
import pubsub from "../../../pubsub";
import {TServiceName} from "../../../constants/ApiServices";
import {PLEASE_WAIT_MESSAGE, REQUEST_MESSAGE} from "../../../constants/Language";
import {global} from "../../../appContext";
import {nsLanguage} from "../../../util/nsLanguage";
import {getHash} from "../../../constants/RouteDefinitions";
import recreateHtml from './recreate.html?raw';

export let template: string = recreateHtml;

export interface IParams {
    data: KnockoutObservableArray<IObjectData>
    dialog: IDialog
    numTotal: number
    objecttypeid: ObjectTypeId
    searchObjectParams: ISearchObjectParams
    getTitle: (num: number) => string
}

export enum Step {
    Start,
    Init,
    DownloadIds,
    Process,
    Complete,
}

export abstract class AbstractDialogOperation {

    Step = Step;
    step: KnockoutObservable<Step | null> = ko.observable(null);
    messageData: KnockoutObservable<IMessageData | null> = ko.observable(null);
    data: number[];
    numTotal: number;
    objecttypeid: ObjectTypeId;
    percentComplete: KnockoutObservable<number | null> = ko.observable(null);
    protected submitSuccess!: number[]
    protected submitError!: { message: string, id: number }[] | any[]

    protected abstract serviceName: TServiceName;
    protected kosub: KOSubscriptionTracker = new KOSubscriptionTracker();

    private reqSearchObject = new PromiseRequest("searchobject");
    protected promiseRequests: PromiseRequest[] = [];
    protected readonly objectDefinition: IObjectDefinition;

    protected maxConcurrentRequests: number = MAX_CONCURRENT_REQUESTS

    constructor(protected params: IParams) {
        pubsub.on(this, "before.close.dialog", (e: any, dialogData: IComponentDialogParams) => {
            if (dialogData?.dialog == this.params?.dialog) {
                this.dispose();
            }
        });
        this.data = getAnyValue<IObjectData[]>(params.data).map((obj: IObjectData) => obj.id);
        this.numTotal = params.numTotal;
        this.objecttypeid = params.objecttypeid
        this.objectDefinition = getObjectDefinition(this.objecttypeid)!;
        this.kosub.subscribe(this.step, this.updateStep.bind(this));
        requestAnimationFrame(() => {
            this.step(Step.Start);
        })
    }

    protected updateStep() {
        switch (this.step()) {

            case Step.Start:
                this.step(Step.Init)
                break

            case Step.Init:
                break;

            case Step.DownloadIds:
                // do we have object ids already?
                if (this.data.length) {
                    this.step(Step.Process);
                } else {
                    // download the ids
                    this.messageData({message: REQUEST_MESSAGE});
                    this.reqSearchObject
                        .request(this.params.searchObjectParams)
                        .then((data: number[]) => {
                            this.data = data;
                            this.step(Step.Process);
                        });
                }
                break;

            case Step.Process:
                this.messageData({message: PLEASE_WAIT_MESSAGE});
                this.process();
                break;

            case Step.Complete:
                pubsub.trigger('search.object.needs.refresh')
                this.messageData(null);
                break;

        }
    }

    protected process() {
        this.submitSuccess = []
        this.submitError = []

        const ids = _.cloneDeep(this.data)

        Promise.resolve()
            .then(() => {
                const doNext = (): Promise<void> => {
                    this.percentComplete((this.submitSuccess.length + this.submitError.length) / this.data.length);
                    this.messageData({message: this.getProcessItemSuccessMessage()})
                    if (!ids.length) {
                        return Promise.resolve()
                    }
                    const id = ids.shift();
                    return this.getPromiseForProcessId(doNext, id)
                }

                const all: Promise<any>[] = []
                for (let i = 0; i < Math.min(this.numTotal, this.maxConcurrentRequests); i++) {
                    all.push(doNext());
                }
                return Promise.all(all)

            })
            .then(() => {
                this.submitSuccess = this.submitSuccess.sort()
                this.submitError = _.sortBy(this.submitError, 'id')
            })
            .then(() => {
                this.onProcessComplete()
            })
            .catch((reason: string) => {
                this.step(Step.Complete);
                this.messageData(createErrorMessage(String(reason)));
            });
    }

    protected getPromiseForProcessId(callback: () => Promise<any>, id: number): Promise<any> {
        const promiseRequest = new PromiseRequest(this.serviceName);
        this.promiseRequests.push(promiseRequest);
        const p = promiseRequest.request(this.getRequestParams(id))
        return p
            .then(() => {
                this.submitSuccess.push(id)
                return callback()
            })
            .catch((reason: string) => {
                this.submitError.push({message: reason, id: id})
                return callback()
            })
    }

    protected abstract getProcessItemSuccessMessage(): string;

    protected abstract successMessage(): string

    protected abstract errorMessage(): string

    protected getRequestParams(id: number): any {
        return {id: id};
    }

    protected onProcessComplete() {
        this.step(Step.Complete);
    }

    /*******************************************************************************************************************
     * View interaction
     ******************************************************************************************************************/

    onCancelClick() {
        this.dispose();
    }

    onContinueClick() {
        this.step((this.step() ?? 0) + 1);
    }

    /*******************************************************************************************************************
     * View helpers / computed properties
     ******************************************************************************************************************/

    isStep(...steps: number[]): boolean {
        return steps.some((step: Step) => step == this.step());
    }

    protected getNumObjectsLabel(num: number): string {
        const objectTypeId = this.params.searchObjectParams?.objecttypeid ?? global.router.currentRoute()?.__objectTypeId ?? this.objecttypeid;
        const manual = this.params.searchObjectParams.manualobjects;
        return nsLanguage.getNumObjectsLabel(num, objectTypeId, manual)
    }

    private getLink(id: number): string {
        return getHash(this.objectDefinition.routeDetails!, {id}) ?? ''
    }

    private createErrorString = createErrorString

    /*******************************************************************************************************************
     * Misc.
     ******************************************************************************************************************/

    dispose() {
        this.kosub.unsuscribeAll();
        this.reqSearchObject.abort();
        this.promiseRequests.forEach((p: PromiseRequest) => p.abort());
    }

}
